<template>
	<c-dialog ref="dialog" title="烘烤任务列表" width="1000px" height="700px" :button="false">
		<div v-if="list.length == 0" class="empty-tip">
			<p>暂无烘烤任务</p>
		</div>
		<div v-else class="bake-list">
			<div class="bake-card" v-for="(item, index) in list" :key="item.id" :class="{'bake-card-cancelled': item.bake_state == 4}">
				<!--主信息行：所有数据单行展示-->
				<div class="card-info-row">
					<span class="info-seq">{{ index + 1 }}</span>
					<div class="info-item">
						<span class="info-label">机台·点位</span>
						<span class="info-value">{{ item.equipment_code || '-' }}·{{ item.point_code != null ? item.point_code : '-' }}</span>
					</div>
					<div class="info-item">
						<span class="info-label">数量</span>
						<span class="info-value info-count">{{ getEffectiveCount(item) }}</span>
					</div>
					<div class="info-item">
						<span class="info-label">目标温度</span>
						<span class="info-value">{{ item.target_temp }}℃<small>±{{ item.temp_tolerance || 0 }}℃</small></span>
					</div>
					<div class="info-item">
						<span class="info-label">目标时长</span>
						<span class="info-value">{{ formatDuration(item.target_duration) }}</span>
					</div>
					<div class="info-item">
						<span class="info-label">已烘烤</span>
						<span class="info-value" :class="{'warming': item.is_warming_up == 1}">{{ formatBakeDuration(item) }}</span>
					</div>
					<div class="info-state">
						<c-state v-if="item.sync_status != 1" dict="bakeSyncStatusDic" :value="item.sync_status != null ? item.sync_status : 0"></c-state>
						<template v-else>
							<c-state dict="bakeStateDic" :value="item.bake_state"></c-state>
							<span v-if="item.is_duration_met == 1 && item.bake_state == 2" class="duration-met-badge">烘烤完成待确认</span>
						</template>
						<span class="info-time">{{ item.create_user_realname || '' }} {{ formatDate(item.create_date) }}</span>
					</div>
				</div>

				<!--下发失败时显示原因-->
				<div v-if="item.sync_status == 2 && item.sync_message" class="sync-message">
					<span class="sync-message-label">失败原因：</span>{{ item.sync_message }}
				</div>

				<!--进度条-->
				<div class="bake-progress">
					<div class="progress-bar">
						<div class="progress-fill" :style="{width: getBakeProgress(item) + '%'}" :class="{'warming': item.is_warming_up == 1, 'met': item.is_duration_met == 1}"></div>
					</div>
					<span class="progress-percent">{{ getBakeProgress(item) }}%</span>
				</div>

				<!--操作行-->
				<div class="card-footer">
					<div class="footer-actions" v-if="auth('b_bake_task_temperature_chart') && item.bake_state != 4">
						<span class="action-link" v-if="item.record_count > 0" @click.stop="toggleChart(item)">
							<svg class="chart-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
								<polyline points="3 17 9 11 13 15 21 7"></polyline>
							</svg>
							{{ chartVisible[item.id] ? '收起曲线' : '温度曲线' }}
						</span>
					</div>
					<div class="footer-actions" v-if="item.bake_state == 2">
						<span v-if="item.sync_status == 2" class="action-link action-retry" @click="confirmRetry(item)">重试下发</span>
						<span v-if="item.sync_status == 2 && auth('b_bake_task_cancel')" class="action-link action-cancel" @click="confirmCancelFailed(item)">取消</span>
						<span v-if="item.is_duration_met != 1 && auth('b_bake_task_repair')" class="action-link action-repair" @click="openRepair(item)">
							<svg class="repair-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
								<path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"></path>
							</svg>
							维修
						</span>
						<span v-if="item.is_duration_met != 1 && item.sync_status != 2 && auth('b_bake_task_cancel')" class="action-link action-cancel" @click="confirmCancel(item)">取消</span>
						<span v-if="item.is_duration_met == 1 && auth('b_bake_task_complete')" class="action-link action-confirm" @click="confirmComplete(item)">确认完成</span>
					</div>
				</div>

				<div v-if="chartVisible[item.id]" class="chart-section">
					<div class="chart-info" v-if="chartData[item.id]">
						<span v-if="chartData[item.id].firstValidTime">达标开始：{{ chartData[item.id].firstValidTime }}</span>
						<span v-if="chartData[item.id].lastValidTime">达标结束：{{ chartData[item.id].lastValidTime }}</span>
					</div>
					<div :ref="'chart_' + item.id" class="chart-box"></div>
				</div>
			</div>
		</div>

		<!--维修弹窗-->
		<c-dialog ref="repairDialog" title="维修操作" @resolve="submitRepair" button-name="确认维修" cancel-button width="400px">
			<div class="repair-info">
				<div class="repair-metrics">
					<div class="repair-metric">
						<span class="repair-metric-label">烘烤数量</span>
						<span class="repair-metric-value">{{ repairItem.plan_count || 0 }}</span>
					</div>
					<div class="repair-metric">
						<span class="repair-metric-label">已维修</span>
						<span class="repair-metric-value repair-value">{{ repairItem.repair_count || 0 }}</span>
					</div>
					<div class="repair-metric">
						<span class="repair-metric-label">本次可维修</span>
						<span class="repair-metric-value">{{ getMaxRepair(repairItem) }}</span>
					</div>
				</div>
			</div>
			<c-form ref="repairForm" unit-width="100%" :vertical="false">
				<c-input type="hidden" name="id"></c-input>
				<c-form-item label="本次维修数量" required>
					<c-input type="number" name="repair_count" decimal="6" :range="[0.000001, maxRepairInput]" placeholder="请输入本次维修数量"></c-input>
				</c-form-item>
			</c-form>
		</c-dialog>
	</c-dialog>
</template>

<script>
import * as echarts from 'echarts'

export default {
	data() {
		return {
			list: [],
			work_id: null,
			task_id: null,
			repairItem: {},
			maxRepairInput: 99999999,
			chartVisible: {},
			chartData: {},
			chartInstances: {}
		}
	},
	methods: {
		open(params) {
			let work_id = null;
			let task_id = null;
			if (params && typeof params == 'object') {
				work_id = params.work_id || null;
				task_id = params.task_id || null;
			} else if (params) {
				work_id = params;
			}
			this.work_id = work_id;
			this.task_id = task_id;
			this.list = [];
			this.chartVisible = {};
			this.chartData = {};
			Object.values(this.chartInstances).forEach(chart => chart && chart.dispose());
			this.chartInstances = {};
			this.$refs.dialog.open();
			this.loadData();
		},

		loadData() {
			if (!this.work_id && !this.task_id) return;
			let data = {};
			if (this.work_id) data.work_id = this.work_id;
			if (this.task_id) data.task_id = this.task_id;
			this.request({
				url: '/bake/task/list',
				data: data,
				loading: true,
				success: data => {
					this.list = data || [];
				}
			});
		},

		formatDuration(minutes) {
			if (!minutes) return '-';
			let m = parseInt(minutes) || 0;
			let hours = Math.floor(m / 60);
			let left = m % 60;
			if (left > 0) {
				return hours + '小时' + left + '分';
			}
			return hours + '小时';
		},

		formatBakeDuration(item) {
			if (item.is_warming_up == 1) return '升温中';
			let minutes = parseInt(item.effective_bake_minutes) || 0;
			if (minutes <= 0) return '0分钟';
			if (minutes < 60) return minutes + '分钟';
			let hours = Math.floor(minutes / 60);
			let left = minutes % 60;
			return left > 0 ? hours + '小时' + left + '分' : hours + '小时';
		},

		toggleChart(item) {
			let id = item.id;
			let visible = !this.chartVisible[id];
			this.$set(this.chartVisible, id, visible);
			if (visible) {
				if (!this.chartData[id]) {
					this.loadChartData(item);
				} else {
					this.$nextTick(() => {
						this.renderChart(item);
					});
				}
			} else {
				if (this.chartInstances[id]) {
					this.chartInstances[id].dispose();
					this.chartInstances[id] = null;
				}
			}
		},

		loadChartData(item) {
			this.request({
				url: '/bake/task/recordList',
				data: {bake_task_id: item.id, range: 'all'},
				loading: true,
				success: data => {
					let records = data || [];
					let targetTemp = parseFloat(item.target_temp) || 0;
					let tolerance = parseFloat(item.temp_tolerance) || 0;
					let lower = targetTemp - tolerance;
					let upper = targetTemp + tolerance;
					let firstValidTime = '';
					let lastValidTime = '';
					records.forEach(r => {
						let temp = r.current_temp;
						if (temp !== null && temp !== undefined && temp !== '') {
							temp = parseFloat(temp);
							if (temp >= lower && temp <= upper) {
								let timeStr = this.formatDate(r.record_time);
								if (!firstValidTime) firstValidTime = timeStr;
								lastValidTime = timeStr;
							}
						}
					});
					this.$set(this.chartData, item.id, {
						records: records,
						targetTemp: targetTemp,
						tolerance: tolerance,
						firstValidTime: firstValidTime,
						lastValidTime: lastValidTime
					});
					this.$nextTick(() => {
						this.renderChart(item);
					});
				}
			});
		},

		renderChart(item) {
			let id = item.id;
			let chartData = this.chartData[id];
			if (!chartData || !chartData.records.length) return;
			let refName = 'chart_' + id;
			let dom = this.$refs[refName];
			if (Array.isArray(dom)) dom = dom[0];
			if (!dom) return;
			if (this.chartInstances[id]) {
				this.chartInstances[id].dispose();
			}
			let chart = echarts.init(dom);
			this.chartInstances[id] = chart;

			let times = chartData.records.map(r => this.formatDate(r.record_time));
			let temps = chartData.records.map(r => {
				let v = r.current_temp;
				return (v === null || v === undefined || v === '') ? null : parseFloat(v);
			});
			let lower = chartData.targetTemp - chartData.tolerance;
			let upper = chartData.targetTemp + chartData.tolerance;
			let markAreas = [];
			let inValid = false;
			let startIndex = -1;
			temps.forEach((temp, index) => {
				if (temp !== null && temp >= lower && temp <= upper) {
					if (!inValid) {
						inValid = true;
						startIndex = index;
					}
				} else {
					if (inValid) {
						markAreas.push([{xAxis: times[startIndex]}, {xAxis: times[index - 1]}]);
						inValid = false;
					}
				}
			});
			if (inValid) {
				markAreas.push([{xAxis: times[startIndex]}, {xAxis: times[times.length - 1]}]);
			}

			let validTemps = temps.filter(t => t !== null);
			let maxTemp = validTemps.length ? Math.max(...validTemps) : 0;

			chart.setOption({
				grid: {top: 30, right: 20, bottom: 25, left: 45},
				tooltip: {
					trigger: 'axis',
					formatter: params => {
						let p = params[0];
						if (p.value === null || p.value === undefined) {
							return p.name + '<br/>温度：无数据';
						}
						return p.name + '<br/>温度：' + p.value + '℃';
					}
				},
				xAxis: {
					type: 'category',
					data: times,
					axisLabel: {fontSize: 11, rotate: 30, interval: Math.floor(times.length / 4)}
				},
				yAxis: {
					type: 'value',
					name: '℃',
					min: 0,
					max: Math.ceil((Math.max(maxTemp, chartData.targetTemp + chartData.tolerance) + 5) / 10) * 10,
					axisLabel: {fontSize: 11},
					splitLine: {lineStyle: {type: 'dashed'}}
				},
				series: [{
					data: temps,
					type: 'line',
					smooth: true,
					connectNulls: false,
					symbol: 'none',
					lineStyle: {color: '#1890ff', width: 2},
					areaStyle: {color: 'rgba(24,144,255,0.08)'},
					markLine: {
						symbol: 'none',
						data: [
							{yAxis: chartData.targetTemp, name: '目标温度', lineStyle: {color: '#52c41a', type: 'dashed'}, label: {formatter: '目标温度', fontSize: 10}}
						]
					},
					markArea: {
						itemStyle: {color: 'rgba(82,196,26,0.12)'},
						data: markAreas
					}
				}]
			});
			chart.resize();
		},

		formatDate(date) {
			if (!date) return '-';
			return new Date(date).format('yyyy-MM-dd hh:mm');
		},

		getEffectiveCount(item) {
			let plan = parseFloat(item.plan_count) || 0;
			let repair = parseFloat(item.repair_count) || 0;
			return Math.max(0, plan - repair);
		},

		getBakeProgress(item) {
			if (item.is_warming_up == 1) return 0;
			let target = parseInt(item.target_duration) || 0;
			let current = parseInt(item.effective_bake_minutes) || 0;
			if (target <= 0) return 0;
			let percent = Math.round((current / target) * 100);
			return Math.min(100, percent);
		},

		getMaxRepair(item) {
			let plan = parseFloat(item.plan_count) || 0;
			let repair = parseFloat(item.repair_count) || 0;
			return Math.max(0, plan - repair);
		},

		confirmCancel(item) {
			this.$confirm({
				message: '确认取消该烘烤任务吗？取消后任务状态将变更为“已取消”，该操作不可恢复。',
				cancelButton: true,
				resolve: () => {
					this.request({
						url: '/bake/task/cancel',
						data: {id: item.id},
						loading: true,
						success: (data, res) => {
							this.$message({message: '取消成功', type: 'success'});
							this.loadData();
							this.$emit('resolve');
						}
					});
				},
				cancel: () => {}
			});
		},

		confirmComplete(item) {
			this.$confirm({
				message: '确认该烘烤任务已完成？确认后烘烤任务将变更为已完成状态。',
				cancelButton: true,
				resolve: () => {
					this.request({
						url: '/bake/task/confirmComplete',
						data: {id: item.id},
						loading: true,
						success: (data, res) => {
							this.$message({message: '确认完成成功', type: 'success'});
							this.loadData();
							this.$emit('resolve');
						}
					});
				},
				cancel: () => {}
			});
		},

		openRepair(item) {
			this.repairItem = item;
			this.maxRepairInput = this.getMaxRepair(item);
			this.$refs.repairForm.clear();
			this.$refs.repairForm.set({id: item.id, repair_count: ''});
			this.$refs.repairDialog.open();
		},

		submitRepair(stop, next) {
			stop();
			this.$refs.repairForm.submit({
				url: '/bake/task/repair',
				rule: {
					repair_count: '请输入维修数量'
				},
				success: (data, res) => {
					next();
					this.$message({message: '维修操作成功', type: 'success'});
					this.loadData();
					this.$emit('resolve');
				}
			});
		},

		confirmRetry(item) {
			this.$confirm({
				message: '确认重新下发该烘烤任务？系统将重新调用三方接口下发任务参数。',
				cancelButton: true,
				resolve: () => {
					this.request({
						url: '/bake/task/retrySend',
						data: {id: item.id},
						loading: true,
						success: (data, res) => {
							this.$message({message: '重试下发成功，等待三方确认', type: 'success'});
							this.loadData();
							this.$emit('resolve');
						}
					});
				}
			});
		},

		confirmCancelFailed(item) {
			this.$confirm({
				message: '确认取消该下发失败的烘烤任务？取消后将物理删除该任务及相关数据，不可恢复。',
				cancelButton: true,
				resolve: () => {
					this.request({
						url: '/bake/task/cancelFailed',
						data: {id: item.id},
						loading: true,
						success: (data, res) => {
							this.$message({message: '取消成功', type: 'success'});
							this.loadData();
							this.$emit('resolve');
						}
					});
				}
			});
		}
	},

	beforeDestroy() {
		Object.values(this.chartInstances).forEach(chart => chart && chart.dispose());
		this.chartInstances = {};
	}
}
</script>

<style lang="stylus" scoped>
.empty-tip
	text-align center
	padding 80px 0
	color #999
	font-size 14px

.bake-list
	padding 16px

.bake-card
	background #fff
	border-radius 8px
	padding 16px 18px
	margin-bottom 12px
	border 1px solid #e8e8e8

.bake-card:last-child
	margin-bottom 0

.bake-card-cancelled
	opacity 0.65
	background #f9f9f9
	border-style dashed

// 主信息行
.card-info-row
	display flex
	align-items center
	gap 20px

.info-seq
	display inline-flex
	align-items center
	justify-content center
	min-width 22px
	height 22px
	padding 0 6px
	font-size 13px
	font-weight 600
	color #666
	background #f2f2f2
	border-radius 50%
	flex-shrink 0

.info-item
	flex-shrink 0

.info-label
	display inline
	font-size 12px
	color #999
	margin-right 4px

.info-value
	display inline
	font-size 14px
	font-weight 600
	color #333

	&.info-count
		font-size 16px
		font-weight 700

	&.warming
		color #d48806

	small
		font-size 11px
		font-weight normal
		color #999
		margin-left 2px

.info-state
	margin-left auto
	display flex
	align-items center
	gap 10px
	flex-shrink 0

.info-time
	font-size 12px
	color #999

// 进度条
.bake-progress
	display flex
	align-items center
	gap 10px
	margin-top 12px

.progress-bar
	flex 1
	height 6px
	background #f0f0f0
	border-radius 3px
	overflow hidden

.progress-fill
	height 100%
	background #1677ff
	border-radius 3px
	transition width 0.4s ease

.progress-fill.warming
	background #d48806

.progress-fill.met
	background #52c41a

.progress-percent
	font-size 12px
	font-weight 600
	color #333
	flex-shrink 0
	min-width 36px
	text-align right

// 操作行
.card-footer
	display flex
	justify-content space-between
	align-items center
	margin-top 10px

.footer-actions
	display flex
	align-items center
	gap 14px

.action-link
	display inline-flex
	align-items center
	gap 5px
	font-size 13px
	color #1890ff
	cursor pointer

	&:hover
		opacity 0.8

.chart-icon
	width 14px
	height 14px

.repair-icon
	width 14px
	height 14px

.action-repair
	color #d48806

.action-cancel
	color #cf1322

.action-confirm
	color #52c41a
	font-weight 600

.action-retry
	color #cf1322
	font-weight 600

.sync-message
	margin-top 8px
	padding 6px 10px
	font-size 12px
	color #cf1322
	background rgba(207, 19, 34, 0.06)
	border-radius 4px
	border 1px solid rgba(207, 19, 34, 0.15)

.sync-message-label
	font-weight 600

.duration-met-badge
	display inline-block
	padding 2px 8px
	font-size 11px
	font-weight 600
	color #52c41a
	background rgba(82, 196, 26, 0.1)
	border 1px solid rgba(82, 196, 26, 0.3)
	border-radius 4px

.cancelled-text
	font-size 13px
	color #999

// 曲线
.chart-section
	margin-top 12px
	padding-top 12px
	border-top 1px solid #f0f0f0

.chart-info
	display flex
	justify-content space-between
	margin-bottom 8px
	font-size 12px
	color #666

.chart-box
	width 100%
	height 220px

// 维修弹窗
.repair-info
	padding 16px
	margin-bottom 16px
	background #f9f9f9
	border-radius 8px

.repair-metrics
	display flex
	justify-content space-between

.repair-metric
	text-align center
	flex 1
	position relative

	&:not(:last-child):after
		content ''
		position absolute
		right 0
		top 15%
		bottom 15%
		width 1px
		background #e8e8e8

.repair-metric-label
	display block
	font-size 12px
	color #999
	margin-bottom 4px

.repair-metric-value
	display block
	font-size 18px
	font-weight 700
	color #333

	&.repair-value
		color #d48806
</style>
